import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Checkbox,
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { generatePerformanceReportPDF } from '@utils/generatePerformanceReportPDF';

interface Player {
  id: string;
  firstName?: string;
  lastName?: string;
}

interface ViewReportsViewProps {
  player: Player;
  coachId?: string;
  selectedSport?: string;
}

interface ReportRecord {
  id: string;
  period: string;
  dateRange: { start: string; end: string };
  submittedAt: string;
  averageRating: number;
  assessmentType: string;
  items: any[];
  sport?: string;
  notes?: string;
  coachId?: string | null;
}

const MAX_COMPARE = 3;

const ViewReportsView: React.FC<ViewReportsViewProps> = ({ player, coachId, selectedSport = 'Badminton' }) => {
  const [reports, setReports] = useState<ReportRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [linkedCoachNameById, setLinkedCoachNameById] = useState<Record<string, string>>({});
  const [selectedForCompare, setSelectedForCompare] = useState<string[]>([]);

  useEffect(() => {
    loadReports();
    loadLinkedCoaches();
    
    // Check if download is requested via URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const downloadId = urlParams.get('download');
    if (downloadId) {
      // Wait for reports to load, then trigger download
      setTimeout(() => {
        const reportToDownload = reports.find(r => r.id === downloadId);
        if (reportToDownload) {
          generateReport(reportToDownload);
          // Clean up URL parameter
          window.history.replaceState({}, '', window.location.pathname + window.location.search.replace(/[?&]download=[^&]*/, '').replace(/^&/, '?'));
        }
      }, 1000);
    }
  }, [player.id, coachId, selectedSport]);
  
  // Also check for download when reports change
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const downloadId = urlParams.get('download');
    if (downloadId && reports.length > 0) {
      const reportToDownload = reports.find(r => r.id === downloadId);
      if (reportToDownload) {
        generateReport(reportToDownload);
        // Clean up URL parameter
        window.history.replaceState({}, '', window.location.pathname + window.location.search.replace(/[?&]download=[^&]*/, '').replace(/^&/, '?'));
      }
    }
  }, [reports]);

  const loadReports = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      // IMPORTANT:
      // - coachId undefined => return ALL evaluations (self + all coaches)
      // - coachId '' => explicit self evaluations only (coach_id IS NULL)
      // - coachId '<uuid>' => filter by that coach only
      if (coachId !== undefined) qs.set('coachId', String(coachId));
      qs.set('sport', selectedSport);
      const response = await fetch(`/api/performance/basic/player/${player.id}?${qs.toString()}`);
      const result = await response.json();
      
      if (result.success) {
        setReports(result.data || []);
      }
    } catch (error) {
      console.error('Error loading reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadLinkedCoaches = async () => {
    try {
      const resp = await fetch(`/api/players/${player.id}/coaches`);
      const data = await resp.json();
      if (data?.success && Array.isArray(data.data)) {
        const map: Record<string, string> = {};
        data.data.forEach((c: any) => {
          if (!c?.id) return;
          const id = String(c.id);
          const name =
            (c.name && String(c.name).trim()) ||
            `${c.firstName || ''} ${c.lastName || ''}`.trim() ||
            c.coachIdCode ||
            `Coach ${id.substring(0, 8)}`;
          map[id] = name;
        });
        setLinkedCoachNameById(map);
      }
    } catch (e) {
      console.error('Error loading linked coaches for reports view:', e);
    }
  };

  const getPeriodLabel = (period: string) => {
    if (period.startsWith('month-')) {
      const monthIndex = parseInt(period.split('-')[1]) - 1;
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      return months[monthIndex] || period;
    }
    if (period.startsWith('quarter-')) {
      const quarter = period.split('-')[1];
      return `Q${quarter}`;
    }
    if (period === 'yearly') {
      return 'Yearly';
    }
    return period;
  };

  const toggleCompare = (reportId: string) => {
    setSelectedForCompare((prev) =>
      prev.includes(reportId)
        ? prev.filter((id) => id !== reportId)
        : prev.length >= MAX_COMPARE
          ? prev
          : [...prev, reportId]
    );
  };

  // Order by evaluation period: earliest = Report 1, latest = Report 2 (so trend = latest vs earlier)
  const selectedReports = useMemo(() => {
    const filtered = reports.filter((r) => selectedForCompare.includes(r.id));
    return [...filtered].sort((a, b) => {
      const dateA = new Date(a.submittedAt || a.dateRange?.start || 0).getTime();
      const dateB = new Date(b.submittedAt || b.dateRange?.start || 0).getTime();
      return dateA - dateB;
    });
  }, [reports, selectedForCompare]);

  const comparisonData = useMemo(() => {
    if (selectedReports.length === 0) return { chartData: [], tableRows: [] };
    const key = (item: any) => `${item.category || ''}|${item.name || ''}`;
    const byKey: Record<string, { category: string; name: string; ratings: (number | null)[] }> = {};
    selectedReports.forEach((report, idx) => {
      (report.items || []).forEach((item: any) => {
        const k = key(item);
        if (!byKey[k]) {
          byKey[k] = { category: item.category || '', name: item.name || '', ratings: [] };
        }
        while (byKey[k].ratings.length < selectedReports.length) byKey[k].ratings.push(null);
        byKey[k].ratings[idx] = item.notApplicable ? null : (item.rating ?? null);
      });
    });
    Object.values(byKey).forEach((r) => {
      while (r.ratings.length < selectedReports.length) r.ratings.push(null);
    });
    const tableRows = Object.values(byKey).sort((a, b) =>
      a.category.localeCompare(b.category) || a.name.localeCompare(b.name)
    );
    const chartData = tableRows.map((row, i) => {
      const point: Record<string, string | number> = { skillName: row.name.length > 20 ? row.name.slice(0, 18) + '…' : row.name, index: i + 1 };
      selectedReports.forEach((_, idx) => {
        const v = row.ratings[idx];
        point[`r${idx}`] = v != null ? v : 0;
      });
      return point;
    });
    return { chartData, tableRows };
  }, [selectedReports]);

  const generateReport = async (report: ReportRecord) => {
    if (!report.items || report.items.length === 0) {
      alert('No evaluation data available to generate report');
      return;
    }
    
    // Use the shared utility function for PDF generation
    await generatePerformanceReportPDF(report as Parameters<typeof generatePerformanceReportPDF>[0], player);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (reports.length === 0) {
    return (
      <Box sx={{ pt: 2 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Submitted Reports
        </Typography>
        <Alert severity="info">
          No submitted reports found.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 2 }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
        Submitted Reports
      </Typography>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox"><strong>Compare</strong></TableCell>
              <TableCell><strong>Sport</strong></TableCell>
              <TableCell><strong>Period</strong></TableCell>
              <TableCell><strong>Assessment Type</strong></TableCell>
              <TableCell><strong>Submitted Date</strong></TableCell>
              <TableCell><strong>Evaluated By</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Average Rating</strong></TableCell>
              <TableCell><strong>Reports</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reports.map((report) => (
              <TableRow key={report.id} hover>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedForCompare.includes(report.id)}
                    onChange={() => toggleCompare(report.id)}
                    disabled={selectedForCompare.length >= MAX_COMPARE && !selectedForCompare.includes(report.id)}
                  />
                </TableCell>
                <TableCell>
                  <Chip 
                    label={report.sport || selectedSport} 
                    color="primary"
                    size="small"
                  />
                </TableCell>
                <TableCell>{getPeriodLabel(report.period)} {new Date(report.dateRange?.start).getFullYear()}</TableCell>
                <TableCell>
                  {(() => {
                    const normalizedType = report.assessmentType === 'quick' || report.assessmentType === 'high-level' 
                      ? 'high-level' 
                      : report.assessmentType === 'advanced' || report.assessmentType === 'low-level'
                      ? 'low-level'
                      : report.assessmentType;
                    return normalizedType === 'low-level' ? 'Skill Assessment Low-level' : 'Skill Assessment High-level';
                  })()}
                </TableCell>
                <TableCell>
                  {report.submittedAt ? new Date(report.submittedAt).toLocaleDateString() : 'N/A'}
                </TableCell>
                <TableCell>
                  {report.coachId
                    ? (linkedCoachNameById[String(report.coachId)] || 'Coach')
                    : 'Self'}
                </TableCell>
                <TableCell>
                  <Chip
                    label={report.coachId ? 'Coach Evaluation' : 'Self Evaluation'}
                    color={report.coachId ? 'success' : 'info'}
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </TableCell>
                <TableCell>
                  <Chip 
                    label={report.averageRating?.toFixed(1) || 'N/A'} 
                    color={report.averageRating >= 4 ? 'success' : report.averageRating >= 3 ? 'warning' : 'error'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PdfIcon />}
                    onClick={() => generateReport(report)}
                  >
                    Download
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {selectedReports.length >= 2 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Compare reports (max {MAX_COMPARE})
          </Typography>

          {comparisonData.chartData.length > 0 && (
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Rating by skill</Typography>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={comparisonData.chartData} margin={{ top: 5, right: 20, left: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="skillName" tick={{ fontSize: 10 }} interval={0} angle={-45} textAnchor="end" height={60} />
                  <YAxis domain={[0, 5]} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  {selectedReports.map((r, idx) => (
                    <Line
                      key={r.id}
                      type="monotone"
                      dataKey={`r${idx}`}
                      name={`${getPeriodLabel(r.period)} ${new Date(r.dateRange?.start).getFullYear()}${r.coachId ? ` (${linkedCoachNameById[String(r.coachId)] || 'Coach'})` : ''}`}
                      stroke={idx === 0 ? '#1976d2' : idx === 1 ? '#2e7d32' : '#ed6c02'}
                      dot={{ r: 3 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          )}

          <Paper sx={{ overflow: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><strong>Category</strong></TableCell>
                  <TableCell><strong>Skill</strong></TableCell>
                  {selectedReports.map((r, idx) => (
                    <TableCell key={r.id} align="center"><strong>Report {idx + 1} rating</strong></TableCell>
                  ))}
                  {selectedReports.length === 2 && <TableCell align="center"><strong>Trend</strong></TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {comparisonData.tableRows.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell>{row.category}</TableCell>
                    <TableCell>{row.name}</TableCell>
                    {row.ratings.map((val, idx) => (
                      <TableCell key={idx} align="center">
                        {val != null ? val : 'N/A'}
                      </TableCell>
                    ))}
                    {selectedReports.length === 2 && (
                      <TableCell align="center">
                        {row.ratings[0] != null && row.ratings[1] != null ? (
                          row.ratings[1] > row.ratings[0] ? <ArrowUpIcon color="success" fontSize="small" />
                          : row.ratings[1] < row.ratings[0] ? <ArrowDownIcon color="error" fontSize="small" />
                          : <RemoveIcon color="action" fontSize="small" />
                        ) : '-'}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default ViewReportsView;

